import React from "react";
import { BRAND } from "../../constants/brand";
import "./kido-invoice.css";

// Decorations for the KIDO invoices — the same pieces as kido-frontend's
// components/kido/invoice/InvoiceParts.jsx, with the doodle drawings copied
// from its KidsDoodles.jsx (only the ones the invoice uses). All inline SVG so
// they print and come out in the html2canvas PDF.

const INK = "#222428";

const ICONS = {
  cloud: (c) => (
    <path d="M14 46 a12 12 0 0 1 2 -24 a16 16 0 0 1 30 -4 a12 12 0 0 1 4 28 z" fill={c} />
  ),
  balloon: (c) => (
    <>
      <ellipse cx="32" cy="24" rx="16" ry="20" fill={c} />
      <ellipse cx="26" cy="16" rx="4" ry="6" fill="rgba(255,255,255,.45)" />
      <path d="M29 44 h6 l-3 4 z" fill={c} />
      <path d="M32 48 q-5 6 0 10 t0 6" fill="none" stroke={INK} strokeOpacity=".35" strokeWidth="1.6" />
    </>
  ),
  star: (c) => (
    <path
      d="M32 6 l7.6 16.4 17.9 2 -13.3 12.2 3.7 17.7 -15.9 -9 -15.9 9 3.7 -17.7 -13.3 -12.2 17.9 -2 z"
      fill={c}
      stroke={c}
      strokeWidth="4"
      strokeLinejoin="round"
    />
  ),
  duck: (c) => (
    <>
      <ellipse cx="34" cy="42" rx="22" ry="14" fill={c} />
      <circle cx="22" cy="24" r="12" fill={c} />
      <path d="M8 24 L1 27 L8 30 Z" fill="#ff8a3d" />
      <circle cx="19" cy="21" r="2" fill={INK} />
      <path d="M36 38 q8 -10 16 -2" fill="none" stroke="rgba(0,0,0,.15)" strokeWidth="3" strokeLinecap="round" />
    </>
  ),
  plane: (c) => (
    <>
      <path d="M4 30 L60 8 L42 56 L32 38 Z" fill={c} />
      <path d="M32 38 L60 8 L26 34 Z" fill="rgba(0,0,0,.12)" />
    </>
  ),
  bear: (c) => (
    <>
      <circle cx="16" cy="16" r="9" fill={c} />
      <circle cx="48" cy="16" r="9" fill={c} />
      <circle cx="32" cy="34" r="22" fill={c} />
      <ellipse cx="32" cy="42" rx="10" ry="8" fill="rgba(255,255,255,.55)" />
      <circle cx="24" cy="30" r="2.4" fill={INK} />
      <circle cx="40" cy="30" r="2.4" fill={INK} />
      <ellipse cx="32" cy="39" rx="3.2" ry="2.4" fill={INK} />
    </>
  ),
  block: (c) => (
    <>
      <rect x="8" y="8" width="48" height="48" rx="8" fill={c} />
      <rect x="15" y="15" width="34" height="34" rx="5" fill="rgba(255,255,255,.35)" />
      <path d="M24 42 L32 22 L40 42 M27 35 h10" fill="none" stroke="#fff" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
};

const COLORS = {
  sky: "#06a0e0",
  sun: "#ffcd3c",
  indigo: "#6c63e0",
  pink: "#ff9fb2",
  mint: "#5fd3a5",
  peach: "#ff9a6b",
  cloud: "#ffffff",
};

export function Doodle({ icon, color = "sky", size = 64, className = "", style }) {
  return (
    <svg className={className} style={style} viewBox="0 0 64 64" width={size} height={size} aria-hidden="true">
      {ICONS[icon](COLORS[color] || color)}
    </svg>
  );
}

// Clouds, stars and doodles over the sky band; planes on the pre-order sheet.
export function SkyArt({ preorder = false }) {
  const items = preorder
    ? [
        { icon: "cloud", color: "#ffffff", top: 14, left: "30%", size: 52 },
        { icon: "plane", color: "indigo", top: 52, left: "44%", size: 30, r: -12 },
        { icon: "star", color: "sun", top: 20, left: "56%", size: 18 },
        { icon: "cloud", color: "#ffffff", top: 80, left: "36%", size: 40 },
        { icon: "plane", color: "sky", top: 96, left: "52%", size: 22, r: 8 },
      ]
    : [
        { icon: "cloud", color: "#ffffff", top: 14, left: "30%", size: 52 },
        { icon: "balloon", color: "pink", top: 40, left: "45%", size: 34, r: -6 },
        { icon: "star", color: "sun", top: 22, left: "56%", size: 18 },
        { icon: "cloud", color: "#ffffff", top: 82, left: "36%", size: 40 },
        { icon: "duck", color: "sun", top: 92, left: "52%", size: 26, r: 6 },
      ];
  return (
    <div className="kdv-sky-art" aria-hidden="true">
      {items.map((d, i) => (
        <Doodle
          key={i}
          icon={d.icon}
          color={d.color}
          size={d.size}
          style={{ top: d.top, left: d.left, "--r": `${d.r || 0}deg` }}
        />
      ))}
      <svg className="kdv-hills" viewBox="0 0 800 40" preserveAspectRatio="none">
        <path d="M0 26 Q120 4 240 22 T480 18 T800 20 V40 H0 Z" fill="#dff5ea" />
        <path d="M0 32 Q160 14 320 30 T640 26 T800 30 V40 H0 Z" fill="#ffffff" />
      </svg>
    </div>
  );
}

// Torn-paper bottom edge for the receipt box.
export function ZigEdge({ color = "#eef7fc" }) {
  const teeth = 30;
  let d = "M0 0 ";
  for (let i = 0; i < teeth; i++) {
    const x = (i * 100) / teeth;
    d += `L${x + 50 / teeth} 10 L${x + 100 / teeth} 0 `;
  }
  return (
    <svg className="kdv-zig" viewBox="0 0 100 10" preserveAspectRatio="none" aria-hidden="true">
      <path d={`${d}Z`} fill={color} />
    </svg>
  );
}

// "Thank you" strip with a teddy and the shop's contacts.
export function ThanksStrip({ note }) {
  return (
    <div className="kdv-thanks">
      <span className="kdv-thanks-art" aria-hidden="true">
        <Doodle icon="bear" color="peach" size={54} />
      </span>
      <div className="kdv-thanks-text">
        <div className="kdv-thanks-title">Thank you for shopping with {BRAND.name}!</div>
        <div className="kdv-thanks-sub">{note || "Packed with care for your little one."}</div>
      </div>
      <div className="kdv-contact">
        <span>{BRAND.phone}</span>
        <span>{BRAND.email}</span>
      </div>
    </div>
  );
}

// Small icon in front of a card label.
export function CardIcon({ icon, color }) {
  return <Doodle icon={icon} color={color} size={22} />;
}
